
import { useEffect } from 'react';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  loading: boolean;
  error?: string;
}

export function LoginModal({ isOpen, onClose, onLogin, loading, error }: LoginModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Botão Fechar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img 
            src="https://public.readdy.ai/ai/img_res/dbab58caa9f2506671a4b02fecc7b35d.jpg"
            alt="FeistyDéx"
            className="w-24 h-24 object-contain"
          />
        </div>

        {/* Título */}
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
          Bem-vindo ao FeistyDéx!
        </h2>
        <p className="text-gray-600 text-center mb-6">
          Faça login para começar sua coleção
        </p>

        {/* Mensagem de Erro */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="text-red-800 text-sm font-semibold mb-1">Erro ao fazer login</p>
                <p className="text-red-700 text-sm">{error}</p>
                {error.includes('popup') && (
                  <div className="mt-3 p-3 bg-white rounded border border-red-200">
                    <p className="text-xs text-gray-700 font-semibold mb-2">Como permitir popups:</p>
                    <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                      <li>Clique no ícone de bloqueio/cadeado na barra de endereço</li>
                      <li>Procure por "Popups" ou "Pop-ups e redirecionamentos"</li>
                      <li>Selecione "Permitir" ou "Sempre permitir"</li>
                      <li>Tente fazer login novamente</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Botão de Login com Google */}
        <button
          onClick={onLogin}
          disabled={loading}
          className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-3 mb-4 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line text-xl animate-spin"></i>
              Conectando...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </>
          )}
        </button>

        {/* Botão Secundário */}
        <button
          onClick={onClose}
          className="w-full text-gray-600 py-2 rounded-lg font-medium hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          Continuar sem fazer login
        </button>

        {/* Nota sobre popups */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <i className="ri-information-line text-blue-600 text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-xs text-blue-800">
              <strong>Importante:</strong> Certifique-se de que os popups estão permitidos no seu navegador para fazer login com sucesso.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
